import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap'
import TabMenu from '../../navigation/TabMenu'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import SettingTabsInfo from '../../../constants/SettingTabsInfo'
import {
  clearEventTriggerError,
  deleteEventTrigger,
  fetchEventTriggers,
  updateEventTrigger,
  type UpdateEventTriggerArg,
} from '../../../modules/EventTriggers'
import { showModal, closeModal } from '../../../modules/UI'
import { BASE_MODAL, CONFIRM_MODAL, ERROR_MODAL } from '../../common/RootModal'
import { toId } from '../../../utils/commonUtils'
import { getErrorString } from '../../../utils/errorUtils'
import EditEventTriggerModal from './EditEventTriggerModal'
import EditStayNotificationModal from './EditStayNotificationModal'
import { comeInEventTriggerIcon, goOutEventTriggerIcon, stayNotificationIcon } from '../../../icons'
import './SettingsEventTriggers.css'

type GroupFilterValue = 'all' | string

const getRecipientList = (trigger: IEventTrigger): string => {
  const recipients = trigger.emails && trigger.emails.length > 0
    ? trigger.emails
    : trigger.users?.map((user) => user.email)
  return recipients && recipients.length > 0 ? recipients.join(', ') : '未設定'
}

const formatMamorioIdentifiersForDisplay = (mamorios?: IEventTriggerMamorio[]): string => {
  if (!mamorios || mamorios.length === 0) {
    return '未設定'
  }

  return mamorios
    .map((mamorio) => {
      const { major, minor, id } = mamorio

      if (major !== null && minor !== null) {
        return `${major}-${minor}`
      }

      if (major !== null) {
        return String(major)
      }

      if (minor !== null) {
        return String(minor)
      }

      return `ID:${id}`
    })
    .join(', ')
}

const formatCooldown = (cooldownMin: number | null): string => {
  if (cooldownMin === null || Number.isNaN(cooldownMin)) return 'なし'
  return `${cooldownMin} 分`
}

const SettingsEventTriggers = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)
  const {
    status,
    error,
    deviceEventTriggers,
    mamoriosEventTriggers,
    stayNotifications,
  } = useAppSelector((state) => state.eventTriggers)
  const [groupFilter, setGroupFilter] = useState<GroupFilterValue>('all')
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false)
  const groupState = useAppSelector((state) => state.group)

  const groups = useMemo(() => team.groups ?? [], [team.groups])

  const selectedGroup = useMemo(() => (
    groupFilter === 'all'
      ? null
      : groups.find((group) => String(group.id) === groupFilter) ?? null
  ), [groups, groupFilter])

  const groupLabel = selectedGroup?.name ?? 'すべてのグループ'

  const resolveGroupForTrigger = useCallback((trigger: IEventTrigger): IGroup | null => {
    const triggerGroupId = Number(trigger.group_id)
    const isValidGroupId = !Number.isNaN(triggerGroupId)

    if (!isValidGroupId) {
      return selectedGroup ?? groupState.group ?? groupState.rootGroup ?? null
    }

    if (selectedGroup && Number(selectedGroup.id) === triggerGroupId) {
      return selectedGroup
    }

    const fromTeam = groups.find((group) => Number(group.id) === triggerGroupId)
    if (fromTeam) return fromTeam

    if (groupState.group && Number(groupState.group.id) === triggerGroupId) {
      return groupState.group
    }

    if (groupState.rootGroup && Number(groupState.rootGroup.id) === triggerGroupId) {
      return groupState.rootGroup
    }

    return selectedGroup ?? groupState.group ?? groupState.rootGroup ?? null
  }, [groupState.group, groupState.rootGroup, groups, selectedGroup])

  const onModalError = useCallback((message?: string | null) => {
    if (!message) return

    dispatch(closeModal({ modalType: ERROR_MODAL }))

    dispatch(showModal({
      modalType: ERROR_MODAL,
      modalProps: {
        message,
      },
    }))
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchEventTriggers({}))

    return () => {
      dispatch(clearEventTriggerError())
    }
  }, [dispatch])

  const handleReload = useCallback(() => {
    if (groupFilter === 'all') {
      dispatch(fetchEventTriggers({}))
      return
    }

    const groupId = toId<GroupId>(groupFilter)
    dispatch(fetchEventTriggers({ groupId }))
  }, [dispatch, groupFilter])

  const handleGroupSelect = useCallback((value: GroupFilterValue) => {
    setGroupFilter(value)
    setIsGroupDropdownOpen(false)

    if (value === 'all') {
      dispatch(fetchEventTriggers({}))
      return
    }

    dispatch(fetchEventTriggers({ groupId: toId<GroupId>(value) }))
  }, [dispatch])

  const hasDeviceTriggers = deviceEventTriggers.length > 0
  const hasMamoriosTriggers = mamoriosEventTriggers.length > 0
  const hasStayNotifications = stayNotifications.length > 0
  const hasAnyTriggers = hasDeviceTriggers || hasMamoriosTriggers || hasStayNotifications

  const tabsInfo = useMemo(
    () => SettingTabsInfo({
      groups: team.groups ?? null,
      deviceEnabled: team.device_enabled,
      qrTagEnabled: team.qr_tag_enabled ?? false,
      rfidTagEnabled: team.rfid_tag_enabled ?? false,
      eventNotificationSettingsEnable: team.event_notification_settings_enable ?? false,
    }),
    [
      team.device_enabled,
      team.event_notification_settings_enable,
      team.groups,
      team.qr_tag_enabled,
      team.rfid_tag_enabled,
    ],
  )

  const openEditModal = useCallback((trigger: IEventTrigger) => {
    const modalComponent = trigger.kind === 'stay'
      ? EditStayNotificationModal
      : EditEventTriggerModal
    const groupForTrigger = resolveGroupForTrigger(trigger)
    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: 'イベント通知を編集',
        trigger,
        onSubmit: async (payload: UpdateEventTriggerArg) => {
          try {
            await dispatch(updateEventTrigger(payload)).unwrap()
          } catch (err: unknown) {
            onModalError(getErrorString(err))
            throw err
          }
        },
        group: groupForTrigger,
        children: React.createElement(modalComponent as React.ComponentType<any>),
      },
    }))
  }, [dispatch, onModalError, resolveGroupForTrigger])

  const confirmDeleteTrigger = useCallback((trigger: IEventTrigger) => {
    const triggerLabel = trigger.name || '名称未設定'
    dispatch(showModal({
      modalType: CONFIRM_MODAL,
      modalProps: {
        message: `${triggerLabel} を削除しますか？`,
        confirmButtonText: '削除する',
        onActionConfirmed: async () => {
          try {
            await dispatch(deleteEventTrigger({
              kind: trigger.kind,
              id: trigger.id,
            })).unwrap()
          } catch (err: unknown) {
            onModalError(getErrorString(err))
          } finally {
            dispatch(closeModal({ modalType: CONFIRM_MODAL }))
          }
        },
      },
    }))
  }, [dispatch, onModalError])

  type EventTriggerSection = {
    label: string
    description: string
    emptyMessage: string
    triggers: IEventTrigger[]
    renderDetails: (trigger: IEventTrigger) => JSX.Element
    icon?: string
  }

  const groupedTriggers: EventTriggerSection[] = [
    {
      label: '入場通知',
      description: '指定デバイスがグループ内のMAMORIOを検知した際にメール・電話・Slackで通知します。',
      emptyMessage: '入場通知のトリガーはまだありません。',
      triggers: deviceEventTriggers,
      icon: comeInEventTriggerIcon,
      renderDetails: (trigger: IEventTrigger) => {
        const deviceTrigger = trigger as IDeviceEventTrigger
        const formattedMamorioIds = formatMamorioIdentifiersForDisplay(deviceTrigger.mamorios)
        return (
          <dl className="event-trigger-card__details">
            <div>
              <dt>対象デバイスID</dt>
              <dd>{deviceTrigger.device_id ?? '未設定'}</dd>
            </div>
            <div>
              <dt>通知クールダウン</dt>
              <dd>{formatCooldown(deviceTrigger.cooldown_min)}</dd>
            </div>
            <div>
              <dt>電話通知</dt>
              <dd>{deviceTrigger.phone_notification ? '有効' : '無効'}</dd>
            </div>
            <div>
              <dt>滞在モード</dt>
              <dd>{deviceTrigger.stay_mode_enabled ? '有効' : '無効'}</dd>
            </div>
            <div>
              <dt>滞在許容検知間隔 (秒)</dt>
              <dd>{deviceTrigger.stay_max_gap_seconds ?? '未設定'}</dd>
            </div>
            <div>
              <dt>対象MAMORIO ID</dt>
              <dd>{formattedMamorioIds}</dd>
            </div>
          </dl>
        )
      },
    },
    {
      label: '退場通知',
      description: 'グループのMAMORIOが未検知となった場合に通知します。',
      emptyMessage: '退場通知のトリガーはまだありません。',
      triggers: mamoriosEventTriggers,
      icon: goOutEventTriggerIcon,
      renderDetails: (trigger: IEventTrigger) => {
        const mamoriosTrigger = trigger as IMamoriosEventTrigger
        return (
          <dl className="event-trigger-card__details">
            <div>
              <dt>閾値 (分)</dt>
              <dd>{mamoriosTrigger.threshold_minutes ?? '未設定'}</dd>
            </div>
            <div>
              <dt>対象MAMORIO ID</dt>
              <dd>{formatMamorioIdentifiersForDisplay(mamoriosTrigger.mamorios)}</dd>
            </div>
            <div>
              <dt>対象デバイスID</dt>
              <dd>{mamoriosTrigger.device_ids?.length ? mamoriosTrigger.device_ids.join(', ') : '未設定'}</dd>
            </div>
            <div>
              <dt>一括紛失通知スキップ</dt>
              <dd>{mamoriosTrigger.skip_all_lost_notification ? '有効' : '無効'}</dd>
            </div>
          </dl>
        )
      },
    },
    {
      label: '滞在時間通知',
      description: 'MAMORIOがデバイス付近に一定時間滞留した場合に通知します。必要に応じて通知先ユーザーを追加できます。',
      emptyMessage: '滞在時間通知はまだありません。',
      triggers: stayNotifications,
      icon: stayNotificationIcon,
      renderDetails: (trigger: IEventTrigger) => {
        const stayNotification = trigger as IStayNotification
        return (
          <dl className="event-trigger-card__details">
            <div>
              <dt>滞在閾値 (分)</dt>
              <dd>{stayNotification.threshold_minutes ?? '未設定'}</dd>
            </div>
            <div>
              <dt>通知クールダウン</dt>
              <dd>{formatCooldown(stayNotification.cooldown_min)}</dd>
            </div>
            <div>
              <dt>許容検知間隔 (秒)</dt>
              <dd>{stayNotification.max_gap_seconds ?? '未設定'}</dd>
            </div>
            <div>
              <dt>対象MAMORIO ID</dt>
              <dd>{formatMamorioIdentifiersForDisplay(stayNotification.mamorios)}</dd>
            </div>
            <div>
              <dt>対象デバイスID</dt>
              <dd>{stayNotification.device_ids?.length ? stayNotification.device_ids.join(', ') : '未設定'}</dd>
            </div>
            <div>
              <dt>電話通知</dt>
              <dd>{stayNotification.phone_notification ? '有効' : '無効'}</dd>
            </div>
            <div>
              <dt>通知先ユーザーID</dt>
              <dd>{stayNotification.user_ids?.length ? stayNotification.user_ids.join(', ') : '未設定'}</dd>
            </div>
            <div>
              <dt>通知先ユーザー</dt>
              <dd>
                {stayNotification.users?.length
                  ? stayNotification.users.map((user) => user.email).join(', ')
                  : '未設定'}
              </dd>
            </div>
          </dl>
        )
      },
    },
  ]

  return (
    <TabMenu tabsInfo={tabsInfo}>
      <div className="settings-event-triggers" data-testid="settings-event-triggers">
        <div className="settings-event-triggers__header">
          <div>
            <h1 className="title">イベント通知の管理</h1>
            <p className="description">
              グループ単位で発火するトリガーを管理します。通知先や対象条件を確認し、運用に合わせて更新してください。
            </p>
          </div>
          <div className="settings-event-triggers__actions">
            <div className="filter-status">
              <div className="filter-title" style={{ width: 'auto' }}>
                対象グループ
              </div>
              <Dropdown
                isOpen={isGroupDropdownOpen}
                toggle={() => setIsGroupDropdownOpen((prev) => !prev)}
                data-testid="event-trigger-group-dropdown"
              >
                <DropdownToggle
                  caret
                  color="white"
                  data-testid="event-trigger-group-dropdown-toggle"
                  disabled={status === 'loading'}
                >
                  {groupLabel}
                </DropdownToggle>
                <DropdownMenu data-testid="event-trigger-group-dropdown-menu">
                  <DropdownItem onClick={() => handleGroupSelect('all')}>
                    すべてのグループ
                  </DropdownItem>
                  {groups.map((group) => (
                    <DropdownItem
                      key={group.id}
                      onClick={() => handleGroupSelect(String(group.id) as GroupFilterValue)}
                    >
                      {group.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleReload}
              disabled={status === 'loading'}
            >
              最新情報に更新
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert" data-testid="event-trigger-error">
            {error}
          </div>
        )}

        {!hasAnyTriggers && status !== 'loading' && !error && (
          <div className="settings-event-triggers__empty" data-testid="event-trigger-empty">
            <p>登録されたイベント通知はまだありません。</p>
            <p>「通知を追加」ボタンから対象デバイスや通知先を設定できます（近日公開）。</p>
          </div>
        )}

        <div className="settings-event-triggers__sections">
          {groupedTriggers.map((section) => (
            <section className="event-trigger-section" key={section.label}>
              <header className="event-trigger-section__header">
                <div className="event-trigger-section__title">
                  {section.icon && (
                    <img
                      src={section.icon}
                      alt={`${section.label}アイコン`}
                      className="event-trigger-section__icon"
                    />
                  )}
                  <h2>{section.label}</h2>
                </div>
                <span className="event-trigger-section__count">
                  {section.triggers.length}
                  件
                </span>
              </header>
              <p className="event-trigger-section__description">
                {section.description}
              </p>
              {section.triggers.length === 0 ? (
                <p className="event-trigger-section__empty">{section.emptyMessage}</p>
              ) : (
                <div className="event-trigger-section__list">
                  {section.triggers.map((trigger) => {
                    const groupForTrigger = resolveGroupForTrigger(trigger)
                    const groupDisplayName = groupForTrigger?.name ?? `ID: ${trigger.group_id}`
                    return (
                      <article className="event-trigger-card" key={`event-trigger-${trigger.kind}-${trigger.id}`}>
                        <div className="event-trigger-card__header">
                          <div className="event-trigger-card__title">
                            <span className="event-trigger-card__name">
                              {trigger.name || '名称未設定'}
                            </span>
                            <span className={`event-trigger-card__status event-trigger-card__status--${trigger.active ? 'active' : 'inactive'}`}>
                              {trigger.active ? '有効' : '無効'}
                            </span>
                          </div>
                          <div className="event-trigger-card__actions">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openEditModal(trigger)}
                            >
                              編集
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => confirmDeleteTrigger(trigger)}
                            >
                              削除
                            </button>
                          </div>
                        </div>
                        <div className="event-trigger-card__meta">
                          <span className="event-trigger-card__group">
                            {`グループ: ${groupDisplayName}`}
                          </span>
                          <span className="event-trigger-card__recipients">
                            {`通知先: ${getRecipientList(trigger)}`}
                          </span>
                          <span className="event-trigger-card__slack">
                            {`Slack: ${trigger.slack_webhook_url || '未設定'}`}
                          </span>
                        </div>
                        <div className="event-trigger-card__body">
                          <dl className="event-trigger-card__details">
                            <div>
                              <dt>メール件名</dt>
                              <dd>{trigger.mail_subject || '未設定'}</dd>
                            </div>
                            <div>
                              <dt>メール本文</dt>
                              <dd className="event-trigger-card__multiline">{trigger.mail_body || '未設定'}</dd>
                            </div>
                          </dl>
                          {section.renderDetails(trigger)}
                        </div>
                      </article>
                    )
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </TabMenu>
  )
}

export default SettingsEventTriggers
